import { formatImpact } from "../../../utils/format";
import { useRangeMode } from "../../impact/context/RangeModeContext";
import {
  isRangeReady,
  metricRange,
  type ScenariosBlock,
} from "../../impact/domain/scenarios";

interface Props {
  scenarios?: ScenariosBlock;
  analysisMode?: "ctg" | "ops";
}

/**
 * Fleet bracket callout — the fleet human-health range where each posture is
 * summed across every project SERVER-SIDE (scenario-consistent), never a
 * per-project min/max. Only rendered in Range mode when the fleet sweep is
 * complete.
 */
export function FleetBracket({ scenarios, analysisMode = "ctg" }: Props) {
  const { mode } = useRangeMode();
  if (mode !== "range" || !isRangeReady(scenarios)) return null;

  const lens = analysisMode === "ops" ? "ops" : "ctg";
  const r = metricRange(scenarios, lens, "dalys");
  if (!r) return null;

  const benefit = r.central >= 0;
  const color = benefit ? "#26bf72" : "#f59e0b";
  const lo = Math.min(r.floor, r.central, r.ceiling);
  const hi = Math.max(r.floor, r.central, r.ceiling);
  const pct = (v: number) => (hi === lo ? 50 : ((v - lo) / (hi - lo)) * 100);
  const fPct = pct(r.floor);
  const cPct = pct(r.central);
  const hPct = pct(r.ceiling);
  const bandLeft = Math.min(fPct, hPct);
  const bandWidth = Math.abs(hPct - fPct);

  const floorLabel = scenarios.floor?.scenario_label ?? "conservative";
  const ceilLabel = scenarios.ceiling?.scenario_label ?? "FRF-off";

  return (
    <div className="mb-4 rounded-xl border border-border bg-surface-700 p-5 shadow">
      <h3 className="text-body-sm font-semibold text-text-primary">
        Fleet human-health range · scenario-consistent
      </h3>
      <p className="mt-0.5 text-caption text-text-muted">
        Each posture summed across all projects server-side — never per-project
        min/max.
      </p>
      <div className="mt-3">
        <span className="font-mono text-2xl font-semibold tabular-nums" style={{ color }}>
          {formatImpact(r.central, "dalys")}
        </span>
        <span className="ml-2 text-caption text-text-muted">
          DALYs {benefit ? "avoided" : "net burden"} · central
        </span>
      </div>
      <div className="relative mt-4 h-2 rounded" style={{ background: `${color}22` }}>
        <span
          className="absolute inset-y-0 rounded"
          style={{ left: `${bandLeft}%`, width: `${bandWidth}%`, background: `${color}80` }}
        />
        <span className="absolute w-0.5" style={{ left: `${fPct}%`, top: "-4px", height: "16px", backgroundColor: "#94a3b8" }} />
        <span className="absolute w-0.5" style={{ left: `${hPct}%`, top: "-4px", height: "16px", backgroundColor: "#94a3b8" }} />
        <span
          className="absolute rounded-full"
          style={{
            left: `${cPct}%`,
            top: "50%",
            width: "13px",
            height: "13px",
            transform: "translate(-50%,-50%)",
            background: color,
            border: "3px solid var(--surface-node, #0b1220)",
          }}
        />
      </div>
      <div className="mt-2 flex justify-between font-mono text-[12px] tabular-nums text-text-muted">
        <span className="text-left">
          <span className="block font-sans text-[9px] font-semibold uppercase tracking-wide">
            Floor · {floorLabel}
          </span>
          {formatImpact(r.floor, "dalys")}
        </span>
        <span className="text-center">
          <span className="block font-sans text-[9px] font-semibold uppercase tracking-wide">
            Central · realized
          </span>
          {formatImpact(r.central, "dalys")}
        </span>
        <span className="text-right">
          <span className="block font-sans text-[9px] font-semibold uppercase tracking-wide">
            Ceiling · {ceilLabel}
          </span>
          {formatImpact(r.ceiling, "dalys")}
        </span>
      </div>
    </div>
  );
}
